import type { ChangeEvent } from 'react';
import { useContext, useEffect, useState } from 'react';

import { Button, ButtonWithLinkStyle, Dialog, Typography } from '@capsule/consumer-design-system';

import * as Sentry from '@sentry/react';
import { push } from 'connected-react-router';
import { AnimatePresence, motion } from 'framer-motion';
import noop from 'lodash/noop';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import GenericErrors from 'src/app/components/Error/Generic';
import type { InsuranceStateContextType } from 'src/app/providers/InsuranceStateProvider';
import { InsuranceStateContext } from 'src/app/providers/InsuranceStateProvider';
import { accountPage } from 'src/app/router/paths';
import { optimisticSetPendingInsurance, segmentTrack, setInsuranceCards } from 'src/app/state/actions';
import { getPreviousPathName, getPrimaryPatientId } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import { uploadImage } from 'src/app/utils/fileUploadUtils';
import { onUploadInsuranceCard } from 'src/app/utils/insuranceUploadUtils';
import { smoothFocus } from 'src/app/utils/smoothFocus';
import { ID_UPLOAD_STATUS, INSURANCE_STRINGS, IS_MOBILE, NAV_HEIGHT } from 'src/consts';

import { InsuranceUploadAndShow } from './InsuranceUploadAndShow';
import { StylizedFooter } from './StylizedFooter';

const StyledUseCapsuleButton = styled(ButtonWithLinkStyle)`
  padding-top: ${({ theme }) => theme.space.s6};
`;
const QrCodeContainer = styled.div`
  border: 1px solid;
  border-color: ${({ theme }) => theme.colors.capsuleGray10};
  border-radius: ${({ theme }) => theme.radii.r100};
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.space.s4};
  gap: ${({ theme }) => theme.space.s5};
`;

const StyledImage = styled.img`
  display: block;
  max-width: 88px;
`;

const EscriptOnboardingPageContainer = styled.div`
  position: fixed;
  margin: 0 auto;
  width: 100%;
  /** we want a defined height to we can have a pinned footer without using position static and suffering z-index. */
  height: calc(100% - ${NAV_HEIGHT / 10}rem);

  box-sizing: border-box;
  & * {
    box-sizing: border-box;
  }
`;

const EscriptOnboardingLayoutContainer = styled.div`
  --mobileGutterSizeOnboardingLayout: ${({ theme }) => theme.space.s5};

  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: ${({ theme }) => theme.space.s4};

  ${({ theme }) => theme.mediaQueries.small} {
    gap: ${({ theme }) => theme.space.s7};
    position: relative;
    width: 100%;
    max-width: 600px;
  }
`;

const StepMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;

  /**
     * No top padding
     * Side padding creates visual gutters
     */
  padding: 0 var(--mobileGutterSizeOnboardingLayout) ${({ theme }) => theme.space.s2};
`;

const CardItemHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const BlueText = styled(Typography)`
  color: ${props => props.theme.colors.capsuleBlue50};
`;

const BlueTextWithBottomPadding = styled(BlueText)`
  padding-bottom: ${props => props.theme.space.s5};
`;

const PageHeader = styled(BlueText)`
  padding: 20px 0;
`;

export const INSURANCE_UPLOAD_SUCCESS_QUERY_PARAM = 'insurance_upload_successful';

const getErrorMessage = (hasUploadedFrontCard: boolean, hasUploadedBackCard: boolean) => {
  if (hasUploadedFrontCard && hasUploadedBackCard) {
    return null;
  } else if (!hasUploadedFrontCard && !hasUploadedBackCard) {
    return 'Please add photos of the front and back of your card';
  } else if (!hasUploadedFrontCard) {
    return 'Please add photos of the front of your card';
  } else if (!hasUploadedBackCard) {
    return 'Please add photos of the back of your card';
  }
  return null;
};

export interface InsuranceCardState {
  url: null | string;
  blob: null | Blob;
  status: string;
  type: string;
}
export const AccountPageAddInsurance = () => {
  const dispatch = useDispatch();
  const [frontCard, setFrontCard] = useState<InsuranceCardState>({
    url: null,
    blob: null,
    status: ID_UPLOAD_STATUS.empty,
    type: INSURANCE_STRINGS.frontCard,
  });
  const [backCard, setBackCard] = useState<InsuranceCardState>({
    url: null,
    blob: null,
    status: ID_UPLOAD_STATUS.empty,
    type: INSURANCE_STRINGS.backCard,
  });

  const primaryPatientId = useSelector(getPrimaryPatientId);
  const previousPathName = useSelector(getPreviousPathName);

  const { saveInsuranceState } = useContext(InsuranceStateContext) as InsuranceStateContextType;

  useEffect(() => {
    dispatch(
      segmentTrack(UNIFIED.viewAddInsurance, {
        source: previousPathName === accountPage.toPath() ? 'account' : 'magic_link',
      })
    );
  }, [dispatch, previousPathName]);

  const hasUploadedFrontCard = !!frontCard?.blob;
  const hasUploadedBackCard = !!backCard?.blob;
  const generatedErrorMessage = getErrorMessage(hasUploadedFrontCard, hasUploadedBackCard);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>, cardType: string) => {
    const onReduceImageSize = (blob: Blob | null) => {
      if (cardType === INSURANCE_STRINGS.frontCard) {
        dispatch(segmentTrack(UNIFIED.addFrontOfInsuranceCard));
        setFrontCard(card => ({
          ...card,
          url: URL.createObjectURL(blob),
          blob: blob,
          status: ID_UPLOAD_STATUS.unverified,
        }));

        if (backCard.status === ID_UPLOAD_STATUS.start) {
          setBackCard(card => ({
            ...card,
            status: ID_UPLOAD_STATUS.empty,
          }));
        }
      } else if (cardType === INSURANCE_STRINGS.backCard) {
        dispatch(segmentTrack(UNIFIED.addBackOfInsuranceCard));
        setBackCard(card => ({
          ...card,
          url: URL.createObjectURL(blob),
          blob: blob,
          status: ID_UPLOAD_STATUS.unverified,
        }));
      }
    };

    const onUploadStart = () => {
      if (cardType === INSURANCE_STRINGS.frontCard) {
        setFrontCard(card => ({ ...card, status: ID_UPLOAD_STATUS.loading }));
      } else if (cardType === INSURANCE_STRINGS.backCard) {
        setBackCard(card => ({ ...card, status: ID_UPLOAD_STATUS.loading }));
      }
    };
    const onUpload = () => {
      onUploadInsuranceCard(event, onReduceImageSize);
    };

    const onUploadError = (error: Error) => {
      if (cardType === INSURANCE_STRINGS.frontCard) {
        setFrontCard(card => ({
          ...card,
          status: ID_UPLOAD_STATUS.empty,
        }));
      } else if (cardType === INSURANCE_STRINGS.backCard) {
        setBackCard(card => ({
          ...card,
          status: ID_UPLOAD_STATUS.empty,
        }));
      }
      Sentry.captureException(error);
    };

    uploadImage(onUploadStart, onUpload, noop, onUploadError);
  };

  const handleDelete = (cardType: string) => {
    if (cardType === INSURANCE_STRINGS.backCard) {
      setBackCard(card => ({
        ...card,
        url: null,
        blob: null,
        status: ID_UPLOAD_STATUS.empty,
      }));
    } else if (cardType === INSURANCE_STRINGS.frontCard) {
      setFrontCard(card => ({
        ...card,
        url: null,
        blob: null,
        status: ID_UPLOAD_STATUS.empty,
      }));
    }
  };

  const handleSubmit = async () => {
    dispatch(segmentTrack(UNIFIED.submitInsurance));
    if (!frontCard.blob) {
      smoothFocus(INSURANCE_STRINGS.frontCard, 0, INSURANCE_STRINGS.frontCard, 250, false);
    } else if (!backCard.blob) {
      smoothFocus(INSURANCE_STRINGS.backCard, 0, INSURANCE_STRINGS.backCard, 250, false);
    } else {
      const isResponseSuccessful = await dispatch(setInsuranceCards(primaryPatientId, frontCard.blob, backCard.blob));
      if (isResponseSuccessful) {
        saveInsuranceState({ isInsuranceSuccessModalActive: true });
        dispatch(push(accountPage.toPath()));
        dispatch(optimisticSetPendingInsurance());
      }
    }
  };

  return (
    <EscriptOnboardingPageContainer>
      <AnimatePresence exitBeforeEnter>
        <motion.div
          css={css`
            height: 100%;
          `}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeIn' }}
          key={`insurance-upload-page`}
        >
          <EscriptOnboardingLayoutContainer>
            <StepMain>
              <PageHeader variant={'heading1'}>Add insurance</PageHeader>
              <BlueTextWithBottomPadding variant={'body'}>
                Typically, your insurance card will contain your Member ID, Rx BIN, Rx Group and Rx PCN.
              </BlueTextWithBottomPadding>
              <GenericErrors />
              <Card>
                <CardItem>
                  <CardItemHeaderRow>
                    {hasUploadedFrontCard && (
                      <>
                        <BlueText variant={'heading3'}>Front of Card</BlueText>
                        <Button variant="text-md" onClick={() => handleDelete(INSURANCE_STRINGS.frontCard)}>
                          REPLACE
                        </Button>
                      </>
                    )}
                  </CardItemHeaderRow>
                  <InsuranceUploadAndShow
                    insuranceCard={frontCard}
                    handleFileUpload={handleFileUpload}
                    handleDelete={() => handleDelete(INSURANCE_STRINGS.frontCard)}
                    bodyText="Front of insurance card"
                  />
                </CardItem>
              </Card>
              <Card>
                <CardItem>
                  <CardItemHeaderRow>
                    {hasUploadedBackCard && (
                      <>
                        <BlueText variant={'heading3'}>Back of Card</BlueText>
                        <Button variant="text-md" onClick={() => handleDelete(INSURANCE_STRINGS.backCard)}>
                          REPLACE
                        </Button>
                      </>
                    )}
                  </CardItemHeaderRow>
                  <InsuranceUploadAndShow
                    insuranceCard={backCard}
                    handleFileUpload={handleFileUpload}
                    handleDelete={() => handleDelete(INSURANCE_STRINGS.backCard)}
                    bodyText="Back of insurance card"
                  />
                </CardItem>
              </Card>
              {!IS_MOBILE && (
                <StyledUseCapsuleButton onClick={() => setIsModalOpen(true)}>
                  Use the Capsule app
                </StyledUseCapsuleButton>
              )}
              <Dialog
                isOpen={isModalOpen}
                onOpenChange={setIsModalOpen}
                title="Download the Capsule app"
                description="The most seamless way to manage your meds"
              >
                <QrCodeContainer>
                  <StyledImage
                    src="/public/get-app-qr-code.svg"
                    alt="QR Code which directs you to download mobile application"
                  />
                  <p>Scan to download the mobile app </p>
                </QrCodeContainer>
              </Dialog>
            </StepMain>

            <StylizedFooter
              error={errorMessage}
              key={`insurance-submit-button`}
              cta1={{
                children: 'Submit',
                onClick: async () => {
                  setErrorMessage(generatedErrorMessage);
                  // If no error message, then submit the photos
                  if (!generatedErrorMessage) {
                    await handleSubmit();
                  }
                },
                'data-cy': 'save-insurance-card-photos',
              }}
            />
          </EscriptOnboardingLayoutContainer>
        </motion.div>
      </AnimatePresence>
    </EscriptOnboardingPageContainer>
  );
};

AccountPageAddInsurance.propTypes = {};
